import { useEffect, useState } from "react";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { reais } from "./lib";

const campo = "w-full h-10 rounded-lg border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary/30";
const botaoPrimario = "h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50";
const botaoNeutro = "h-10 px-4 rounded-lg border text-sm font-semibold hover:bg-muted";

/** Quantidade a cancelar ou transferir: só aparece quando o item tem mais de 1 unidade. */
function Quantidade({ item, valor, onChange }) {
  const total = Number(item?.quantidade || 1);
  if (total <= 1 || total % 1) return null;
  return (
    <div>
      <label className="block text-xs font-medium mb-1">Quantas unidades? <span className="text-muted-foreground font-normal">(tem {total})</span></label>
      <div className="flex gap-1.5 flex-wrap">
        {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)}
            className={`h-9 min-w-[2.5rem] px-2 rounded-lg border text-sm font-semibold ${valor === n ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}>{n}</button>
        ))}
      </div>
    </div>
  );
}

export function CancelarItemDialog({ item, open, onClose, onConfirmar }) {
  const [quantidade, setQuantidade] = useState(1);
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  useEffect(() => { if (open) { setQuantidade(Number(item?.quantidade || 1)); setMotivo(""); } }, [open, item]);
  const total = Number(item?.quantidade || 1);
  const confirmar = async () => {
    setEnviando(true);
    try { await onConfirmar(quantidade >= total ? null : quantidade, motivo.trim() || null); onClose(); } finally { setEnviando(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-e2e="comanda-cancelar-item">
        <DialogHeader>
          <DialogTitle>Cancelar item</DialogTitle>
          <DialogDescription>{item?.nome} · {reais(Number(item?.preco || 0) * total)}. O estoque do que for cancelado volta.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Quantidade item={item} valor={quantidade} onChange={setQuantidade} />
          <div>
            <label className="block text-xs font-medium mb-1">Motivo <span className="text-muted-foreground font-normal">(opcional)</span></label>
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={120} placeholder="Ex.: cliente desistiu" className={campo} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className={botaoNeutro}>Voltar</button>
            <button type="button" onClick={confirmar} disabled={enviando} className="h-10 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
              {enviando ? "Cancelando…" : `Cancelar ${quantidade >= total ? "o item" : `${quantidade} de ${total}`}`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ListaDeOutras({ outras, escolhidas, onToggle, unica }) {
  if (!outras.length) return <p className="text-sm text-muted-foreground">Não há outra comanda aberta.</p>;
  return (
    <div className="max-h-64 overflow-y-auto divide-y rounded-lg border">
      {outras.map((c) => {
        const marcada = escolhidas.includes(c.id);
        return (
          <button key={c.id} type="button" onClick={() => onToggle(c.id)} data-e2e="comanda-opcao"
            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left ${marcada ? "bg-primary/10" : "hover:bg-muted/60"}`}>
            <span className={`w-5 h-5 ${unica ? "rounded-full" : "rounded-md"} border-2 grid place-items-center shrink-0 ${marcada ? "bg-primary border-primary" : "border-border"}`}>
              {marcada && <span className="w-2 h-2 rounded-full bg-primary-foreground" />}
            </span>
            <span className="text-lg font-black tabular-nums w-9">{String(c.numero).padStart(2, "0")}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold truncate">{c.cliente_nome || "Sem nome"}</span>
              <span className="block text-xs text-muted-foreground">{c.itens || 0} itens · {reais(c.total)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function TransferirDialog({ item, outras, open, onClose, onConfirmar }) {
  const [quantidade, setQuantidade] = useState(1);
  const [destino, setDestino] = useState(null);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => { if (open) { setQuantidade(Number(item?.quantidade || 1)); setDestino(null); } }, [open, item]);
  const total = Number(item?.quantidade || 1);
  const confirmar = async () => {
    if (!destino) return;
    setEnviando(true);
    try { await onConfirmar(destino, quantidade >= total ? null : quantidade); onClose(); } finally { setEnviando(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-e2e="comanda-transferir">
        <DialogHeader>
          <DialogTitle>Transferir item</DialogTitle>
          <DialogDescription>{item?.nome}: escolha a comanda que vai receber. O item já pronto na cozinha continua pronto.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Quantidade item={item} valor={quantidade} onChange={setQuantidade} />
          <ListaDeOutras outras={outras} escolhidas={destino ? [destino] : []} onToggle={setDestino} unica />
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className={botaoNeutro}>Voltar</button>
            <button type="button" onClick={confirmar} disabled={!destino || enviando} className={botaoPrimario}>{enviando ? "Transferindo…" : "Transferir"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function JuntarDialog({ comanda, outras, open, onClose, onConfirmar }) {
  const [escolhidas, setEscolhidas] = useState([]);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => { if (open) setEscolhidas([]); }, [open]);
  const toggle = (id) => setEscolhidas((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const confirmar = async () => {
    if (!escolhidas.length) return;
    setEnviando(true);
    try { await onConfirmar(escolhidas); onClose(); } finally { setEnviando(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-e2e="comanda-juntar">
        <DialogHeader>
          <DialogTitle>Juntar comandas nesta</DialogTitle>
          <DialogDescription>Os itens das comandas marcadas vêm para a comanda {String(comanda?.numero || "").padStart(2, "0")}, que fecha tudo numa conta só. As outras ficam como "juntadas".</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <ListaDeOutras outras={outras} escolhidas={escolhidas} onToggle={toggle} />
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className={botaoNeutro}>Voltar</button>
            <button type="button" onClick={confirmar} disabled={!escolhidas.length || enviando} className={botaoPrimario}>
              {enviando ? "Juntando…" : `Juntar ${escolhidas.length || ""}`.trim()}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CancelarComandaDialog({ comanda, open, onClose, onConfirmar }) {
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  useEffect(() => { if (open) setMotivo(""); }, [open]);
  const confirmar = async () => {
    if (motivo.trim().length < 3) return;
    setEnviando(true);
    try { await onConfirmar(motivo.trim()); onClose(); } finally { setEnviando(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-e2e="comanda-cancelar">
        <DialogHeader>
          <DialogTitle>Cancelar a comanda {String(comanda?.numero || "").padStart(2, "0")}</DialogTitle>
          <DialogDescription>Todos os itens são cancelados e o estoque volta. Não dá para desfazer.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1">Motivo</label>
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={120} placeholder="Ex.: cliente foi embora" className={campo} autoFocus />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className={botaoNeutro}>Voltar</button>
            <button type="button" onClick={confirmar} disabled={motivo.trim().length < 3 || enviando} className="h-10 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
              {enviando ? "Cancelando…" : "Cancelar comanda"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
